/* eslint-disable max-lines */
import { Stack } from "@mui/material";
import { type BreakPolicyRule } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { AttendanceVerificationMethods } from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/AttendanceVerificationMethods";
import {
  type NfcTag,
  type SaveNfcTagLocationParams,
} from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/types";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { SettingRow } from "@src/appV2/redesign/components/SettingRow";
import { type UseModalState } from "@src/lib/ui-react";

import { LoadingButton } from "../../../components/LoadingButton";
import { TextInput } from "../../../components/TextInput";
import { EditPolicyDialog } from "../../components/EditPolicyDialog";
import { SettingEditableContent } from "../../components/SettingEditableContent";
import { SettingToggle } from "../../components/SettingToggle";
import { type SettingsPermissions } from "../../types";
import { AutoVerifyThresholdSettingRow } from "./AutoVerifyThresholdSettingRow";
import { BreakPolicyEditor } from "./BreakPolicyEditor/BreakPolicyEditor";

interface TimeAttendanceSectionViewProps {
  autoDeclinationReason: string | undefined;
  autoDeclineReasonModalState: UseModalState;
  autoVerifyThresholdInMinutes: number;
  breakPolicies: BreakPolicyRule[] | undefined;
  hostedTimeDeviceId: number;
  isAutoDeclineVisible: boolean;
  isAutoDeclinationEnabled: boolean;
  isAutoVerifyVisible: boolean;
  isCaliforniaFacility: boolean;
  isHostedTimeLoading: boolean;
  isHostedTimeUpdating: boolean;
  isRestrictOverworkVisible: boolean;
  isSkipWorkplaceSignatureEnabled: boolean;
  isSkipWorkplaceSignatureVisible: boolean;
  isUpdating: boolean;
  mandatoryBreakPolicyModalState: UseModalState;
  mandatoryBreakPolicyText: string;
  nfcRequestList: NfcTag[];
  permissions: SettingsPermissions;
  preventEarlyClockIn: boolean;
  providesRestBreaks: boolean;
  requiresLunchBreak: boolean;
  restBreakPolicyModalState: UseModalState;
  restBreakPolicyText: string;
  restrictOverwork: boolean;
  workplaceId: string;
  onAutoDeclineReasonSave: (reason: string) => void;
  onAutoDeclineToggle: (checked: boolean) => void;
  onAutoVerifyThresholdChange: (value: number) => void;
  onBreakPolicySave: (rules: BreakPolicyRule[]) => Promise<boolean>;
  onDeleteNfcTag: (tagId: string) => Promise<void>;
  onHostedTimeDeviceIdChange: (value: number) => void;
  onHostedTimeSave: () => void;
  onMandatoryBreakPolicySave: (policyText: string) => void;
  onMandatoryBreakToggle: (checked: boolean) => void;
  onPreventEarlyClockInToggle: (checked: boolean) => void;
  onRequestNfcTags: (numberOfTags: number) => Promise<void>;
  onRestBreakPolicySave: (policyText: string) => void;
  onRestBreaksToggle: (checked: boolean) => void;
  onRestrictOverworkToggle: (checked: boolean) => void;
  onSaveNfcTagLocation: (parameters: SaveNfcTagLocationParams) => Promise<void>;
  onSkipWorkplaceSignatureToggle: (checked: boolean) => void;
}

export function TimeAttendanceSectionView({
  autoDeclinationReason,
  autoDeclineReasonModalState,
  autoVerifyThresholdInMinutes,
  breakPolicies,
  hostedTimeDeviceId,
  isAutoDeclineVisible,
  isAutoDeclinationEnabled,
  isAutoVerifyVisible,
  isCaliforniaFacility,
  isHostedTimeLoading,
  isHostedTimeUpdating,
  isRestrictOverworkVisible,
  isSkipWorkplaceSignatureEnabled,
  isSkipWorkplaceSignatureVisible,
  isUpdating,
  mandatoryBreakPolicyModalState,
  mandatoryBreakPolicyText,
  nfcRequestList,
  permissions,
  preventEarlyClockIn,
  providesRestBreaks,
  requiresLunchBreak,
  restBreakPolicyModalState,
  restBreakPolicyText,
  restrictOverwork,
  workplaceId,
  onAutoDeclineReasonSave,
  onAutoDeclineToggle,
  onAutoVerifyThresholdChange,
  onBreakPolicySave,
  onDeleteNfcTag,
  onHostedTimeDeviceIdChange,
  onHostedTimeSave,
  onMandatoryBreakPolicySave,
  onMandatoryBreakToggle,
  onPreventEarlyClockInToggle,
  onRequestNfcTags,
  onRestBreakPolicySave,
  onRestBreaksToggle,
  onRestrictOverworkToggle,
  onSaveNfcTagLocation,
  onSkipWorkplaceSignatureToggle,
}: TimeAttendanceSectionViewProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);
  const isFacilityOrCbhAdmin = permissions.isFacilityAdmin || permissions.isAdmin;

  return (
    <Stack>
      <SettingToggle
        checked={preventEarlyClockIn}
        disabled={!isFacilityOrCbhAdmin || isUpdating}
        subtitle={tu(
          "We will not charge you for clocked time before a shift's posted start time. Only applies to shifts worked after making this change. Please note that this may affect punctuality."
        )}
        title={tu("Prevent professionals from clocking in early")}
        onChange={onPreventEarlyClockInToggle}
      />

      {isAutoVerifyVisible && (
        <AutoVerifyThresholdSettingRow
          thresholdInMinutes={autoVerifyThresholdInMinutes}
          disabled={isUpdating}
          onChange={onAutoVerifyThresholdChange}
        />
      )}

      {isRestrictOverworkVisible && (
        <SettingToggle
          checked={restrictOverwork}
          disabled={!isFacilityOrCbhAdmin || isUpdating}
          subtitle={tu(
            "When enabled, workplace users cannot send invites that would cause a professional to work more than 17 hours within a 24-hour period."
          )}
          title={tu("Restrict shift invites that would exceed 17 hours per day")}
          onChange={onRestrictOverworkToggle}
        />
      )}

      {isSkipWorkplaceSignatureVisible && (
        <SettingToggle
          checked={isSkipWorkplaceSignatureEnabled}
          disabled={isUpdating}
          subtitle={tu(
            "When enabled, workers can submit their timesheet without a workplace signature."
          )}
          title={tu("Skip workplace signature on timesheet submission")}
          onChange={onSkipWorkplaceSignatureToggle}
        />
      )}

      <SettingToggle
        checked={requiresLunchBreak}
        disabled={!isFacilityOrCbhAdmin || isUpdating}
        title={tu("Require mandatory break")}
        onChange={onMandatoryBreakToggle}
      />

      {requiresLunchBreak && (
        <Stack sx={{ paddingLeft: { xs: 0, md: 4 } }}>
          <SettingEditableContent
            buttonLabel={tu("Edit policy")}
            disabled={!isFacilityOrCbhAdmin || isUpdating}
            subtitle={mandatoryBreakPolicyText}
            title={tu("Mandatory break policy")}
            onEdit={mandatoryBreakPolicyModalState.openModal}
          />

          {permissions.isAdmin && (
            <Stack sx={{ paddingBottom: 4 }}>
              <BreakPolicyEditor
                savedRules={breakPolicies}
                isSaving={isUpdating}
                onSave={onBreakPolicySave}
              />
            </Stack>
          )}
        </Stack>
      )}

      {isAutoDeclineVisible && (
        <>
          <SettingToggle
            checked={isAutoDeclinationEnabled}
            disabled={!isFacilityOrCbhAdmin || isUpdating}
            subtitle={tu(
              "When enabled, expiring break payment requests will be declined. When disabled, they will be approved."
            )}
            title={tu("Automatically decline expiring break payment requests")}
            onChange={onAutoDeclineToggle}
          />

          {isAutoDeclinationEnabled && autoDeclinationReason && (
            <SettingEditableContent
              buttonLabel={tu("Edit declination reason")}
              disabled={!isFacilityOrCbhAdmin || isUpdating}
              title={tu("Declination reason")}
              onEdit={autoDeclineReasonModalState.openModal}
            />
          )}
        </>
      )}

      {isCaliforniaFacility && (
        <>
          <SettingToggle
            checked={providesRestBreaks}
            disabled={!isFacilityOrCbhAdmin || isUpdating}
            subtitle={tu(
              "When enabled, the rest period policy will be visible to professionals in the app."
            )}
            title={tu("Provide optional paid rest periods")}
            onChange={onRestBreaksToggle}
          />

          {providesRestBreaks && (
            <SettingEditableContent
              buttonLabel={tu("Edit paid rest period policy")}
              disabled={!isFacilityOrCbhAdmin || isUpdating}
              title={tu("Paid rest period policy")}
              onEdit={restBreakPolicyModalState.openModal}
            />
          )}
        </>
      )}

      {permissions.isAdmin && !isHostedTimeLoading && (
        <SettingRow
          action={
            <LoadingButton
              isLoading={isHostedTimeUpdating}
              variant="outlined"
              onClick={onHostedTimeSave}
            >
              {tu("Save")}
            </LoadingButton>
          }
          subtitle={
            <TextInput
              fullWidth
              helperText={tu(
                "Entering a device ID enables HostedTime punch sync for this workplace. Leave blank to keep the integration disabled."
              )}
              inputProps={{ "aria-label": tu("HostedTime device ID") }}
              placeholder={tu("Enter device ID")}
              size="small"
              sx={{ marginTop: 1 }}
              type="number"
              value={hostedTimeDeviceId || ""}
              onChange={(event) => {
                onHostedTimeDeviceIdChange(Number(event.target.value) || 0);
              }}
            />
          }
          title={tu("HostedTime integration device ID")}
        />
      )}

      {isFacilityOrCbhAdmin && (
        <Stack sx={(theme) => ({ paddingTop: theme.spacing(3), paddingBottom: theme.spacing(1) })}>
          <AttendanceVerificationMethods
            isCbhAdmin={permissions.isAdmin}
            nfcRequestList={nfcRequestList}
            workplaceId={workplaceId}
            onDeleteNfcTag={onDeleteNfcTag}
            onRequestNfcTags={onRequestNfcTags}
            onSaveNfcTagLocation={onSaveNfcTagLocation}
          />
        </Stack>
      )}

      <EditPolicyDialog
        fieldName={tu("Policy")}
        initialContent={mandatoryBreakPolicyText}
        isSaving={isUpdating}
        maxCharacters={1500}
        minCharacters={30}
        modalState={mandatoryBreakPolicyModalState}
        title={tu("Review and edit your facility's mandatory break policy")}
        onSave={onMandatoryBreakPolicySave}
      />

      {isCaliforniaFacility && (
        <EditPolicyDialog
          fieldName={tu("Policy")}
          initialContent={restBreakPolicyText}
          isSaving={isUpdating}
          maxCharacters={1500}
          minCharacters={30}
          modalState={restBreakPolicyModalState}
          title={tu("Review and edit the paid rest period policy")}
          onSave={onRestBreakPolicySave}
        />
      )}

      {isAutoDeclineVisible && (
        <EditPolicyDialog
          fieldName={tu("Reason")}
          initialContent={autoDeclinationReason ?? ""}
          isSaving={isUpdating}
          maxCharacters={2000}
          minCharacters={60}
          modalState={autoDeclineReasonModalState}
          title={tu("Enter a default declination reason to use at the end of the review window")}
          onSave={onAutoDeclineReasonSave}
        />
      )}
    </Stack>
  );
}
/* eslint-enable max-lines */
